import { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Kanban, Mail, Menu, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import KanbanBoard from '@/components/internal/KanbanBoard';
import AddProspectForm from '@/components/internal/AddProspectForm';
import EmailTemplatesPage from '@/pages/EmailTemplates';
import Guide from '@/pages/Guide';
import type { Prospect } from '@/components/internal/ProspectCard';

const TEAM_MEMBERS = ['Zain', 'Stiofan'];

type Page = 'pipeline' | 'templates' | 'guide';

const Internal = () => {
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [filterAssignee, setFilterAssignee] = useState('');
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState<Page>('pipeline');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const fetchProspects = useCallback(async () => {
    const { data } = await supabase
      .from('prospects')
      .select('*')
      .order('created_at', { ascending: false });
    if (data) setProspects(data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchProspects(); }, [fetchProspects]);

  const navItems = [
    { id: 'pipeline' as Page, label: 'Pipeline', icon: Kanban },
    { id: 'templates' as Page, label: 'Email Templates', icon: Mail },
    { id: 'guide' as Page, label: 'Guide', icon: BookOpen },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-56' : 'w-14'
        } border-r border-border bg-card flex flex-col shrink-0 transition-all duration-200`}
      >
        <div className="p-3 border-b border-border flex items-center gap-2">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
          >
            <Menu className="w-4 h-4" />
          </button>
          {sidebarOpen && (
            <div className="flex items-center gap-2 overflow-hidden">
              <h1 className="font-funnel font-bold text-lg text-foreground">qwip</h1>
              <span className="text-muted-foreground text-xs">/ internal</span>
            </div>
          )}
        </div>

        <nav className="flex-1 p-2 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActivePage(item.id)}
              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors ${
                activePage === item.id
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <item.icon className="w-4 h-4 shrink-0" />
              {sidebarOpen && <span className="truncate">{item.label}</span>}
            </button>
          ))}

          {/* Add Prospect shortcut */}
          {sidebarOpen && (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted mt-4 border border-dashed border-border"
            >
              <Plus className="w-4 h-4 shrink-0" />
              <span className="truncate">New Prospect</span>
            </button>
          )}
          {!sidebarOpen && (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full flex items-center justify-center p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted mt-4 border border-dashed border-border"
              title="New Prospect"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {activePage === 'pipeline' && (
          <>
            {/* Pipeline header */}
            <div className="border-b border-border bg-card px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
                  <button
                    onClick={() => setFilterAssignee('')}
                    className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                      !filterAssignee ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    All
                  </button>
                  {TEAM_MEMBERS.map((m) => (
                    <button
                      key={m}
                      onClick={() => setFilterAssignee(filterAssignee === m ? '' : m)}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        filterAssignee === m ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={fetchProspects}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button size="sm" onClick={() => setShowAddForm(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Add Prospect
                  </Button>
                </div>
              </div>
            </div>

            {/* Board */}
            <div className="p-4 flex-1 overflow-auto">
              {loading ? (
                <div className="flex items-center justify-center h-64 text-muted-foreground">Loading...</div>
              ) : (
                <KanbanBoard
                  prospects={prospects}
                  onRefresh={fetchProspects}
                  filterAssignee={filterAssignee}
                />
              )}
            </div>
          </>
        )}

        {activePage === 'templates' && <EmailTemplatesPage />}
        {activePage === 'guide' && <Guide />}
      </div>

      {showAddForm && (
        <AddProspectForm
          onClose={() => setShowAddForm(false)}
          onAdded={fetchProspects}
        />
      )}
    </div>
  );
};

export default Internal;
